/**
 * Phase 28A — Operator Activity Engine (frontend).
 *
 * Tracks mouse / keyboard / touch / focus / visibility / route changes
 * and posts a heartbeat to /api/system/operator-heartbeat every 30s
 * while ACTIVE or PASSIVE.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from './api';

const HEARTBEAT_MS = 30000;
const PASSIVE_AFTER_S = 60;
const IDLE_AFTER_S = 600;

const TAB_MAP = {
  '/app/command':    'COMMAND',
  '/app/discovery':  'DISCOVERY',
  '/app/portfolio':  'COMMAND',
  '/app/sim':        'COMMAND',
  '/app/truth':      'TRUTH',
  '/app/alt':        'ALT_MARKETS',
  '/app/alt-markets':'ALT_MARKETS',
  '/app/manual':     'MANUAL',
  '/app/learning':   'TRUTH',
  '/app/audit':      'TRUTH',
};

export function useOperatorActivity() {
  const lastActivity = useRef(Date.now());
  const [state, setState] = useState('ACTIVE');
  const location = useLocation();

  useEffect(() => {
    const bump = () => { lastActivity.current = Date.now(); };
    const evs = ['mousemove','mousedown','touchstart','keydown','wheel','scroll'];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const onVisibility = () => {
      if (document.hidden) setState('BACKGROUND');
      else { lastActivity.current = Date.now(); setState('ACTIVE'); }
    };
    document.addEventListener('visibilitychange', onVisibility);
    const tick = setInterval(() => {
      if (document.hidden) return; // keep BACKGROUND
      const age = (Date.now() - lastActivity.current) / 1000;
      if (age > IDLE_AFTER_S) setState('IDLE');
      else if (age > PASSIVE_AFTER_S) setState('PASSIVE');
      else setState('ACTIVE');
    }, 5000);
    return () => {
      evs.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(tick);
    };
  }, []);

  // Heartbeat — every HEARTBEAT_MS while ACTIVE/PASSIVE
  useEffect(() => {
    const send = async () => {
      if (state === 'BACKGROUND') return;
      try {
        const tab = TAB_MAP[location?.pathname] || null;
        await api.post('/system/operator-heartbeat', {
          state,
          active_tab: tab,
          visible: !document.hidden,
          focused: document.hasFocus(),
          last_activity_ms: Date.now() - lastActivity.current,
        });
      } catch {}
    };
    send();
    const iv = setInterval(send, HEARTBEAT_MS);
    return () => clearInterval(iv);
  }, [state, location?.pathname]);

  return state;
}
